import { useRuntimeConfig } from "#app"
import { useShopwareContext } from "#imports"

/*
Usage:

const { recaptcha } = useRecaptchaV3()
let isNotABot = await recaptcha()

if (isNotABot) {
    submitForm()
} else {
    console.error('Recaptcha failed, please try again.')
}
*/

export function useRecaptchaV3 () {
    const config = useRuntimeConfig().public
    const { apiClient } = useShopwareContext()

    async function recaptcha() {
        // Ignore recaptcha in dev mode
        if(process.env.NODE_ENV === 'development' || config.testMode === 'true') {
            return true
        }

        await initRecaptchaApi()

        const token = await executeRecaptcha()

        if (!token) {
            return false
        }

        try {
            const { data } = await apiClient.invoke('verifyRecaptcha post /recaptcha', {
                body: {
                    'g-recaptcha-response': token,
                }
            })

            if (data.body.success === true && data.body.score >= 0.3) {
                return true
            }

            return false
        } catch (e: unknown) {
            // When the server's own call to Google's siteverify fails, DmfPwaRecaptcha's
            // RecaptchaController answers 500 with `{"body":{...}}` instead of Shopware's
            // `{"errors":[...]}` envelope. The api-client cannot read that shape, so it
            // throws an ApiClientError carrying no details whatsoever ("Failed request"),
            // which used to escape to the global error handler.
            console.error('Recaptcha verification request failed:', e)

            return false
        }
    }

    async function executeRecaptcha() {
        return await window.grecaptcha?.execute(config.googleRecaptchaSiteKey, { action: 'submit' })
    }

    function initRecaptchaApi(): Promise<void> | undefined {
        if (window.grecaptcha != null) {
            return
        }

        return new Promise((resolve, reject) => {
            const scriptElement = document.createElement('script')
            scriptElement.type = 'text/javascript'
            scriptElement.src = `https://www.google.com/recaptcha/api.js?render=${config.googleRecaptchaSiteKey}`
            scriptElement.id = 'recaptcha-api'

            document.head.appendChild(scriptElement)

            scriptElement.addEventListener('load', () => {
                window.grecaptcha?.ready(function () {
                    resolve()
                })
            })

            scriptElement.addEventListener('error', () => {
                reject(new Error(`${scriptElement.src} failed to load.`))
            })
        });
    }

    return {
        recaptcha,
        initRecaptchaApi
    }
}
